import _jsx from '@babel/runtime/helpers/jsx';
import React from 'react';
import cn from 'classnames';
import { useLazyStyles } from '@vpp-frontend-components/hooks';
import _extends from '@babel/runtime/helpers/extends';
import { PaymentIcon } from '@vpp-frontend-components/payment-icon';
import { MediaAction, Media } from '@vpp-frontend-components/media';
import { StyledLabel } from '@vpp-frontend-components/styled-label';
import { componentEnhance } from '@vpp-frontend-components/component-enhance';
import { List } from '@vpp-frontend-components/list';
import { Iframe } from '@vpp-frontend-components/iframe';
import { Icon } from '@vpp-frontend-components/icon';
import { ActionModal } from '@vpp-frontend-components/action-modal';
import { colornames, backgroundColor, onIframeModalResize } from '@vpp-frontend-components/common';
const namespace$3 = 'ui-vip-payment_methods';

/**
 * Method component
 * @typedef {import('../../index').Method}
 */
const Method = _ref => {
  let {
    title = null,
    icons = null,
    action = null,
    subtitle = null,
    disclaimers = null
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, title && /*#__PURE__*/_jsx(StyledLabel, {
    className: cn(`${namespace$3}__title`),
    color: title.color,
    font_family: title.font_family,
    font_size: title.font_size,
    as: "p",
    text: componentEnhance.jsx(title.text || title, title === null || title === void 0 ? void 0 : title.values)
  }), subtitle && subtitle.length > 0 ? subtitle.map(_ref2 => {
    let {
      color,
      font_family,
      font_size,
      text
    } = _ref2;
    return /*#__PURE__*/_jsx(StyledLabel, {
      className: cn(`${namespace$3}__subtitle`),
      color: color,
      font_family: font_family,
      font_size: font_size,
      as: "p",
      text: text
    });
  }) : subtitle && /*#__PURE__*/_jsx("p", {
    className: cn(`${namespace$3}__subtitle`)
  }, void 0, subtitle.text), /*#__PURE__*/_jsx(PaymentIcon, {
    icons: icons
  }), List && disclaimers && /*#__PURE__*/React.createElement(List, _extends({
    style: disclaimers.listStyle
  }, disclaimers)), action && /*#__PURE__*/_jsx(MediaAction, {
    target: action.target,
    htmlTarget: action.html_target,
    track: action.track,
    className: `${namespace$3}__action`,
    label: {
      text: action.label.text
    }
  }));
};
Method.displayName = 'Method';
const namespace$2 = 'ui-vip-payment_methods';
const getLabel = function (icon, title, backgroundColor$1) {
  let useMobileStyles = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  return /*#__PURE__*/_jsx(Media, {
    figure: icon && /*#__PURE__*/React.createElement(Icon, _extends({}, icon, {
      className: cn(colornames(icon.color), `${namespace$2}__card`)
    })),
    className: cn(useMobileStyles ? `${namespace$2}__container__mobile` : `${namespace$2}__container`, backgroundColor(backgroundColor$1), colornames(icon.color)),
    title: title
  });
};

/**
 * Message component
 * @typedef {import('../../index').Message}
 */
const Message = _ref => {
  let {
    icon,
    title,
    action,
    backgroundColor,
    useMobileStyles = false
  } = _ref;
  return /*#__PURE__*/_jsx(ActionModal, {
    componentAsLabel: getLabel(icon, title, backgroundColor, useMobileStyles),
    modalClassName: "ui-pdp-iframe-modal",
    modalTitle: action.modal_title,
    url: action.target,
    closeModalLabel: action.close_modal_label,
    isMobile: useMobileStyles
  }, void 0, /*#__PURE__*/_jsx(Iframe, {
    title: action.label.text,
    src: action.target,
    scrolling: "no",
    onMessage: onIframeModalResize('vip:modal-content:loaded', 'height', '.ui-pdp-iframe-modal', '.andes-modal__header')
  }));
};
const namespace$1 = 'ui-vip-payment_methods';

/**
 * PaymentMethodsDesktop component
 * @typedef {import('../../index').PaymentMethodsDesktop}
 */
const PaymentMethodsDesktop = _ref => {
  let {
    messages = null,
    payment_methods = null,
    stylesMedium = null,
    className = '',
    runCatchErrorBoundary = () => {}
  } = _ref;
  try {
    const refView = useLazyStyles(stylesMedium);
    return /*#__PURE__*/React.createElement("div", {
      ref: refView,
      className: cn(namespace$1, className)
    }, messages === null || messages === void 0 ? void 0 : messages.map(_ref2 => {
      let {
        icon,
        title,
        action,
        background_color: backgroundColor
      } = _ref2;
      return /*#__PURE__*/_jsx(Message, {
        icon: icon,
        title: title,
        action: action,
        backgroundColor: backgroundColor
      }, title);
    }), payment_methods === null || payment_methods === void 0 ? void 0 : payment_methods.map(_ref3 => {
      let {
        title,
        icons,
        subtitle,
        action,
        disclaimers
      } = _ref3;
      return /*#__PURE__*/_jsx(Method, {
        icons: icons,
        title: title,
        subtitle: subtitle,
        styles: stylesMedium,
        action: action,
        disclaimers: disclaimers
      }, title.text);
    }));
  } catch (error) {
    /* istanbul ignore next */
    return runCatchErrorBoundary(error);
  }
};
const namespace = 'ui-vip-payment_methods';

/**
 * PaymentMethodsMobile component
 * @typedef {import('../../index').PaymentMethodsMobile}
 */
const PaymentMethodsMobile = _ref => {
  let {
    messages = null,
    payment_methods = null,
    stylesMedium = null,
    className = '',
    runCatchErrorBoundary = () => {},
    show_grouped = false
  } = _ref;
  try {
    const refView = useLazyStyles(stylesMedium);
    const isGroupedLayout = Boolean(show_grouped);
    const renderMessages = () => {
      if (!Array.isArray(messages) || (messages === null || messages === void 0 ? void 0 : messages.length) === 0) {
        return null;
      }
      return messages === null || messages === void 0 ? void 0 : messages.map(_ref2 => {
        let {
          icon,
          title,
          action,
          background_color: backgroundColor
        } = _ref2;
        return /*#__PURE__*/_jsx(Message, {
          icon: icon,
          title: title,
          action: action,
          backgroundColor: backgroundColor,
          useMobileStyles: isGroupedLayout
        }, title === null || title === void 0 ? void 0 : title.text);
      });
    };
    const renderIndividualMethods = () => {
      if (isGroupedLayout || !Array.isArray(payment_methods) || (payment_methods === null || payment_methods === void 0 ? void 0 : payment_methods.length) === 0) {
        return null;
      }
      return payment_methods === null || payment_methods === void 0 ? void 0 : payment_methods.map(_ref3 => {
        let {
          title,
          icons,
          subtitle
        } = _ref3;
        return /*#__PURE__*/_jsx(Method, {
          icons: icons,
          title: title,
          subtitle: subtitle,
          styles: stylesMedium
        }, title === null || title === void 0 ? void 0 : title.text);
      });
    };
    const renderGroupedPaymentIcon = () => {
      if (!isGroupedLayout || !Array.isArray(payment_methods) || (payment_methods === null || payment_methods === void 0 ? void 0 : payment_methods.length) === 0) {
        return null;
      }
      const representativeMethod = payment_methods[0];
      if (!(representativeMethod !== null && representativeMethod !== void 0 && representativeMethod.icons)) {
        return null;
      }
      return /*#__PURE__*/_jsx(PaymentIcon, {
        icons: representativeMethod.icons
      });
    };
    return /*#__PURE__*/React.createElement("div", {
      ref: refView,
      id: "paymentsContainer",
      className: cn(namespace, isGroupedLayout && `${namespace}__mobile`, className)
    }, renderMessages(), renderIndividualMethods(), renderGroupedPaymentIcon());
  } catch (error) {
    /* istanbul ignore next */
    return runCatchErrorBoundary(error);
  }
};
PaymentMethodsMobile.displayName = 'PaymentMethods';
export { Message, Method, PaymentMethodsDesktop, PaymentMethodsMobile };
